import { useNotificationStore } from '../notifications/notificationStore.js';
import { Icon } from './Icon.js';

/**
 * The account-menu switch for system notifications.
 *
 * It lives behind a user gesture on purpose: browsers only honour a permission
 * prompt raised from a click, and asking on page load is the pattern every
 * guideline tells you not to use. Until someone clicks this, the app has never
 * asked.
 *
 * Every state the browser can be in has its own wording, because the answers to
 * "why am I not getting notifications?" are genuinely different - a denial we
 * cannot undo from script, an origin that can never be granted, and a preference
 * the user can flip right here.
 */
export function NotificationToggle() {
  const availability = useNotificationStore((state) => state.availability);
  const permission = useNotificationStore((state) => state.permission);
  const enabled = useNotificationStore((state) => state.enabled);
  const requesting = useNotificationStore((state) => state.requesting);
  const outcome = useNotificationStore((state) => state.lastOutcome);
  const enable = useNotificationStore((state) => state.enable);
  const disable = useNotificationStore((state) => state.disable);

  if (availability !== 'available') {
    return (
      <p className="menu-note">
        <Icon name="bellOff" size={15} />
        <span>
          {availability === 'insecure-context'
            ? 'System notifications need an HTTPS connection. This page is served over plain HTTP, so the browser will not allow them.'
            : availability === 'needs-install'
              ? 'On iPhone and iPad, notifications work in the installed app: tap Share, then "Add to Home Screen", and open Orbit Sync from there.'
              : 'This browser does not support system notifications.'}
        </span>
      </p>
    );
  }

  if (permission === 'denied') {
    return (
      <p className="menu-note">
        <Icon name="bellOff" size={15} />
        <span>
          Notifications are blocked for this site. Allow them from the padlock menu in the address
          bar to turn them back on.
        </span>
      </p>
    );
  }

  const on = enabled && permission === 'granted';

  return (
    <>
      <button
        type="button"
        className="menu-item"
        role="menuitemcheckbox"
        aria-checked={on}
        disabled={requesting}
        onClick={() => {
          if (on) {
            disable();
            return;
          }

          void enable();
        }}
      >
        <Icon name={on ? 'bell' : 'bellOff'} size={15} />
        Message notifications
        <span className={on ? 'menu-state menu-state--on' : 'menu-state'}>
          {requesting ? 'Asking…' : on ? 'On' : 'Off'}
        </span>
      </button>

      {outcome === 'dismissed' ? (
        <p className="menu-note">
          <Icon name="alertCircle" size={15} />
          <span>
            The browser did not show its prompt - look for a bell icon at the right of the address
            bar and allow notifications there.
          </span>
        </p>
      ) : null}
    </>
  );
}
